// WorkbenchContext — anything in the workspace can open a file in the workbench (the right-hand panel), ask the one
// "are you sure?" before deleting something, or show a short note at the foot of the screen.
import React from 'react';
import type { ConfirmAsk } from '../components/workspace/Confirm';

export type BenchView = 'page' | 'pdf' | 'text' | 'code' | 'image' | 'audio' | 'video' | 'file' | 'pad';

export interface BenchItem {
  title: string;
  view: BenchView;
  /** A URL the browser can load directly (an /a/ page, a public media route). */
  url?: string;
  /** A Library record (it can be deleted or put on a project). Without a url, its file is fetched with the PIN token and shown from memory. */
  libraryId?: string;
  fileName?: string;
  /** 📝 A pad beside the conversation: shown live from the workspace's pads, by its id. */
  padId?: string;
  /** Shown under the title: who made it, when. */
  meta?: string;
}

export interface Workbench {
  /** The EmCog's name, for the words the workspace shows. */
  name: string;
  open: ((item: BenchItem) => void) | null;
  confirm: (ask: ConfirmAsk) => void;
  note: (text: string) => void;
}

export const WorkbenchContext = React.createContext<Workbench>({ name: 'Your EmCog', open: null, confirm: () => { /* outside the workspace */ }, note: () => { /* outside the workspace */ } });

/** How a file opens, from its name. */
export function viewForName(name: string): BenchView {
  if (/\.pdf$/i.test(name)) return 'pdf';
  if (/\.(md|markdown|txt)$/i.test(name)) return 'text';
  if (/\.html?$/i.test(name)) return 'page';
  if (/\.(png|jpe?g|webp|gif|svg)$/i.test(name)) return 'image';
  if (/\.(mp3|wav|ogg|m4a|flac)$/i.test(name)) return 'audio';
  if (/\.(mp4|webm|mov)$/i.test(name)) return 'video';
  if (/\.(py|js|ts|tsx|jsx|json|csv|sh|ps1|sql|css|xml|ya?ml|rs|go|java|c|cpp|h)$/i.test(name)) return 'code';
  return 'file';
}
